import areEmailDomainsWellFormed from '@UI/Pages/Administration/Sso/SsoFormContainer/FormData/constraint/areEmailDomainsWellFormed';
import isClientSecretRequired from '@UI/Pages/Administration/Sso/SsoFormContainer/FormData/constraint/isClientSecretRequired';
import type IdentityProviderView from '@UI/Store/Sso/Views/IdentityProviderView';
import { AbstractValidatedClass, Joi, ValidatedClass, constraint } from '@verclam/form-validator';

export type IdentityProviderFormArgs = {
	mode: string;
	isEnabled: boolean;
	issuerUrl: string;
	clientId: string;
	clientSecret: string;
	emailDomains: readonly string[];
	/** Drives `isClientSecretRequired`: never sent to the server, only read by the constraint. */
	isCreation: boolean;
};

/**
 * `ignoreNullValue` is deliberately absent: with it, a `null` value would be treated as valid and
 * `isClientSecretRequired` would never even run (`guides/forms.md`). `mode` has no default: an empty
 * string fails `Joi.valid(...)` until the administrator actually picks one, exactly like a required
 * field with no pre-selection.
 */
@ValidatedClass()
class IdentityProviderFormData extends AbstractValidatedClass<IdentityProviderFormArgs> {
	@constraint(
		Joi.string().uri({ scheme: 'https' }).required(),
		() => 'L’URL du fournisseur doit être une adresse HTTPS valide.',
	)
	public readonly issuerUrl: string;

	@constraint(Joi.string().required(), () => 'L’identifiant client est obligatoire.')
	public readonly clientId: string;

	@constraint(isClientSecretRequired, 'Le secret client est obligatoire.')
	public readonly clientSecret: string;

	@constraint(areEmailDomainsWellFormed, 'Renseignez au moins un domaine de messagerie valide.')
	public readonly emailDomains: readonly string[];

	@constraint(Joi.string().valid('coexistence', 'exclusive').required(), () => 'Choisissez un mode.')
	public readonly mode: string;

	public readonly isEnabled: boolean;
	public readonly isCreation: boolean;

	constructor(data: IdentityProviderFormArgs) {
		super(data);
		this.issuerUrl = data.issuerUrl;
		this.clientId = data.clientId;
		this.clientSecret = data.clientSecret;
		this.emailDomains = data.emailDomains;
		this.mode = data.mode;
		this.isEnabled = data.isEnabled;
		this.isCreation = data.isCreation;
		Object.freeze(this);
	}

	static createEmpty(): IdentityProviderFormData {
		return new IdentityProviderFormData({
			mode: '',
			isEnabled: false,
			issuerUrl: '',
			clientId: '',
			clientSecret: '',
			emailDomains: [],
			isCreation: true,
		});
	}

	/** The secret is never prefilled (SEC-12): the server never serves it back, so an edit form starts
	 * from a blank field whose absence on submission means "keep the one already registered." */
	static fromView(identityProvider: IdentityProviderView): IdentityProviderFormData {
		return new IdentityProviderFormData({
			mode: identityProvider.mode.value,
			isEnabled: identityProvider.isEnabled,
			issuerUrl: identityProvider.issuerUrl,
			clientId: identityProvider.clientId,
			clientSecret: '',
			emailDomains: [...identityProvider.emailDomains],
			isCreation: false,
		});
	}

	setMode(mode: string): this {
		return this.cloneWith({ mode } as Partial<IdentityProviderFormArgs>);
	}

	setIsEnabled(isEnabled: boolean): this {
		return this.cloneWith({ isEnabled } as Partial<IdentityProviderFormArgs>);
	}

	setIssuerUrl(issuerUrl: string): this {
		return this.cloneWith({ issuerUrl } as Partial<IdentityProviderFormArgs>);
	}

	setClientId(clientId: string): this {
		return this.cloneWith({ clientId } as Partial<IdentityProviderFormArgs>);
	}

	setClientSecret(clientSecret: string): this {
		return this.cloneWith({ clientSecret } as Partial<IdentityProviderFormArgs>);
	}

	setEmailDomains(emailDomains: readonly string[]): this {
		return this.cloneWith({ emailDomains } as Partial<IdentityProviderFormArgs>);
	}
}

export default IdentityProviderFormData;
